<template>
  <Teleport to="body">
    <div v-if="open" class="modal fade show d-block" tabindex="-1" @click.self="close">
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-heart-pulse text-primary me-2"></i>
              {{ pregnancy ? 'Editar gestação' : 'Registrar gestação' }}
            </h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>

          <div class="modal-body">
            <div class="row g-4">
              <div class="col-12">
                <div class="fw-bolder fs-6 mb-1">Datação</div>
                <div class="text-muted fs-7">Informe a DUM, a USG precoce ou ambas. A USG prevalece quando a DUM é incerta ou discorda além do limite da faixa de IG.</div>
              </div>

              <div class="col-md-4">
                <label class="form-label">DUM</label>
                <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.lmp_date }"
                       v-model="form.lmp_date" :max="today">
                <small class="text-danger" v-if="errors.lmp_date">{{ errors.lmp_date }}</small>
                <div class="form-check form-check-sm form-check-custom form-check-solid mt-2">
                  <input class="form-check-input" type="checkbox" id="prenatal-lmp-reliable" v-model="form.lmp_reliable">
                  <label class="form-check-label fs-7" for="prenatal-lmp-reliable">DUM confiável</label>
                </div>
              </div>
              <div class="col-md-4">
                <label class="form-label">Data da USG precoce</label>
                <input type="date" class="form-control form-control-sm" :class="{ 'is-invalid': errors.us_exam_date }"
                       v-model="form.us_exam_date" :max="today">
                <small class="text-danger" v-if="errors.us_exam_date">{{ errors.us_exam_date }}</small>
              </div>
              <div class="col-md-4">
                <label class="form-label">IG na USG</label>
                <div class="input-group input-group-sm">
                  <input type="number" min="0" max="20" class="form-control" :class="{ 'is-invalid': errors.us_ga_weeks }"
                         v-model="form.us_ga_weeks" placeholder="sem">
                  <span class="input-group-text">s</span>
                  <input type="number" min="0" max="6" class="form-control" :class="{ 'is-invalid': errors.us_ga_days }"
                         v-model="form.us_ga_days" placeholder="dias">
                  <span class="input-group-text">d</span>
                </div>
                <small class="text-danger" v-if="errors.us_ga_weeks || errors.us_ga_days">{{ errors.us_ga_weeks || errors.us_ga_days }}</small>
              </div>

              <div class="col-12" v-if="preview">
                <div class="prenatal-dating-preview rounded p-3">
                  <div class="d-flex flex-wrap gap-5">
                    <div>
                      <div class="text-muted fs-8">IG hoje</div>
                      <div class="fw-bolder fs-5">{{ preview.label }}</div>
                    </div>
                    <div>
                      <div class="text-muted fs-8">DPP (Nägele)</div>
                      <div class="fw-bolder fs-5">{{ formatDateBr(preview.eddDate) }}</div>
                    </div>
                    <div>
                      <div class="text-muted fs-8">Datação adotada</div>
                      <div class="fw-bolder fs-6">
                        {{ preview.method === 'ultrasound' ? 'USG precoce' : 'DUM' }}
                        <span v-if="preview.redatedByUltrasound" class="badge badge-light-warning ms-1">redatada pela USG</span>
                      </div>
                    </div>
                    <div>
                      <div class="text-muted fs-8">Trimestre</div>
                      <div class="fw-bolder fs-6">{{ preview.trimester }}º</div>
                    </div>
                  </div>
                  <div v-for="warning in preview.warnings" :key="warning" class="text-warning fs-7 mt-2">
                    <i class="bi bi-exclamation-triangle me-1"></i>{{ warning }}
                  </div>
                </div>
              </div>

              <div class="col-12"><div class="separator separator-dashed"></div></div>

              <div class="col-md-3">
                <label class="form-label">Peso pré-gestacional (kg)</label>
                <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors.pre_weight_kg }"
                       v-model="form.pre_weight_kg">
                <small class="text-danger" v-if="errors.pre_weight_kg">{{ errors.pre_weight_kg }}</small>
              </div>
              <div class="col-md-3">
                <label class="form-label">Altura (cm)</label>
                <input type="text" inputmode="decimal" class="form-control form-control-sm" :class="{ 'is-invalid': errors.height_cm }"
                       v-model="form.height_cm">
                <small class="text-danger" v-if="errors.height_cm">{{ errors.height_cm }}</small>
              </div>
              <div class="col-md-2">
                <label class="form-label">IMC</label>
                <div class="form-control form-control-sm form-control-solid">{{ bmi ?? '—' }}</div>
              </div>
              <div class="col-md-2">
                <label class="form-label">Nº de fetos</label>
                <input type="number" min="1" max="6" class="form-control form-control-sm" :class="{ 'is-invalid': errors.fetus_count }"
                       v-model="form.fetus_count">
                <small class="text-danger" v-if="errors.fetus_count">{{ errors.fetus_count }}</small>
              </div>
              <div class="col-md-2">
                <label class="form-label">Risco</label>
                <select class="form-select form-select-sm" v-model="form.risk">
                  <option v-for="option in riskOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
                </select>
              </div>
              <div class="col-12">
                <label class="form-label">Observações</label>
                <textarea class="form-control form-control-sm" rows="2" v-model="form.notes"></textarea>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-sm btn-light" @click="close">Cancelar</button>
            <button type="button" class="btn btn-sm btn-primary" :disabled="saving" @click="save">
              <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
              Salvar
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="open" class="modal-backdrop fade show"></div>
  </Teleport>
</template>

<script>
import http from '../../../helpers/http';
import Alert from '../../../helpers/alert';
import {
  RISK_OPTIONS,
  bodyMassIndex,
  buildPayload,
  estimateDating,
  formatDateBr,
  patientBaseUrl,
  todayIso,
  validationErrors,
} from './prenatal';

const PREVIEW_DEBOUNCE_MS = 300;

function initialForm(pregnancy) {
  return {
    lmp_date: pregnancy?.lmp_date ?? '',
    lmp_reliable: pregnancy?.lmp_reliable ?? true,
    us_exam_date: pregnancy?.us_exam_date ?? '',
    us_ga_weeks: pregnancy?.us_ga_weeks ?? '',
    us_ga_days: pregnancy?.us_ga_days ?? '',
    pre_weight_kg: pregnancy?.pre_weight_kg ?? '',
    height_cm: pregnancy?.height_cm ?? '',
    fetus_count: pregnancy?.fetus_count ?? 1,
    risk: pregnancy?.risk ?? 'habitual',
    notes: pregnancy?.notes ?? '',
  };
}

export default {
  name: 'PregnancyFormModal',
  emits: ['close', 'saved'],
  props: {
    open: Boolean,
    pregnancy: { type: Object, default: null },
    professionalId: { type: [Number, String], required: true },
    patientId: { type: [Number, String], required: true },
    placeId: { type: [Number, String], required: true },
  },
  data() {
    return {
      form: initialForm(this.pregnancy),
      errors: {},
      saving: false,
      preview: null,
      previewTimeout: null,
      today: todayIso(),
      riskOptions: RISK_OPTIONS,
    };
  },
  computed: {
    bmi() {
      return bodyMassIndex(this.form.pre_weight_kg, this.form.height_cm);
    },
    datingInput() {
      return {
        lmpDate: this.form.lmp_date,
        lmpReliable: this.form.lmp_reliable,
        usExamDate: this.form.us_exam_date,
        usGaWeeks: this.form.us_ga_weeks,
        usGaDays: this.form.us_ga_days,
      };
    },
  },
  watch: {
    open(isOpen) {
      if (isOpen) {
        this.form = initialForm(this.pregnancy);
        this.errors = {};
        this.refreshPreview();
      }
    },
    datingInput: {
      deep: true,
      handler() {
        clearTimeout(this.previewTimeout);
        this.previewTimeout = setTimeout(this.refreshPreview, PREVIEW_DEBOUNCE_MS);
      },
    },
  },
  mounted() {
    this.refreshPreview();
  },
  beforeUnmount() {
    clearTimeout(this.previewTimeout);
  },
  methods: {
    formatDateBr,
    refreshPreview() {
      this.preview = estimateDating(this.datingInput, this.today);
    },
    close() {
      this.$emit('close');
    },
    async save() {
      this.saving = true;
      this.errors = {};

      const payload = buildPayload(this.form, ['pre_weight_kg', 'height_cm', 'us_ga_weeks', 'us_ga_days', 'fetus_count']);
      const base = `${patientBaseUrl(this.professionalId, this.patientId)}/pregnancies`;

      try {
        const response = this.pregnancy
          ? await http.put(`${base}/${this.pregnancy.id}`, payload)
          : await http.post(base, { ...payload, id_place: this.placeId });

        Alert.toastSuccess(this.pregnancy ? 'Gestação atualizada.' : 'Gestação registrada.');
        this.$emit('saved', response.data);
      } catch (error) {
        this.errors = validationErrors(error);

        const message = this.errors.id_pregnancy || this.errors.id_patient;
        if (message || !Object.keys(this.errors).length) {
          Alert.toastError(message || error.response?.data?.message || 'Falha ao salvar a gestação.');
        }
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.prenatal-dating-preview {
  background: #f8f5ff;
  border: 1px dashed #780f5a33;
}
</style>
